import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as matchService from '../services/matchService';
import { MATCH_KEY } from '../utils/query-keys';
import type {
  GetMatchesCountData,
  GetNetworkMatchesData,
  SubmitRejectionFeedbackData,
  UpdateMatchStatusData,
  TrackIceBreakerUsageData,
  SubmitMatchFeedbackWithReasonsData,
} from '../types/match';

export const useOnboardingMatches = () =>
  useQuery({
    queryKey: [...MATCH_KEY, 'onboardingMatches'],
    queryFn: matchService.getOnboardingMatches,
  });

export const useNetworkMatches = (data: GetNetworkMatchesData) =>
  useQuery({
    queryKey: [...MATCH_KEY, 'networkMatches', data],
    queryFn: () => matchService.getNetworkMatches(data),
  });

export const useMatchCounts = (data: GetMatchesCountData) =>
  useQuery({
    queryKey: [...MATCH_KEY, 'matchCounts', data],
    queryFn: () => matchService.getMatchCounts(data),
  });

export const useUpdateMatchStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateMatchStatusData) =>
      matchService.updateMatchStatus(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...MATCH_KEY, 'networkMatches'] });
      qc.invalidateQueries({ queryKey: [...MATCH_KEY, 'onboardingMatches'] });
      qc.invalidateQueries({ queryKey: [...MATCH_KEY, 'matchCounts'] });
    },
  });
};

export const useSubmitRejectionFeedback = () => {
  return useMutation({
    mutationFn: (data: SubmitRejectionFeedbackData) =>
      matchService.submitRejectionFeedback(data),
  });
};

// === Phase 1.1: Match Explanation ===
export const useMatchExplanation = (matchId: string, enabled = true) =>
  useQuery({
    queryKey: [...MATCH_KEY, 'explanation', matchId],
    queryFn: () => matchService.getMatchExplanation(matchId),
    enabled: enabled && !!matchId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

export const useRefreshMatchExplanation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (matchId: string) =>
      matchService.getMatchExplanation(matchId, true),
    onSuccess: (_, matchId) => {
      qc.invalidateQueries({ queryKey: [...MATCH_KEY, 'explanation', matchId] });
    },
  });
};

// === Phase 1.2: Ice Breakers ===
export const useIceBreakers = (matchId: string, enabled = true) =>
  useQuery({
    queryKey: [...MATCH_KEY, 'iceBreakers', matchId],
    queryFn: () => matchService.getIceBreakers(matchId),
    enabled: enabled && !!matchId,
    staleTime: 24 * 60 * 60 * 1000, // 24 hours
  });

export const useTrackIceBreakerUsage = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: TrackIceBreakerUsageData) =>
      matchService.trackIceBreakerUsage(data),
    onSuccess: (_, { match_id }) => {
      qc.invalidateQueries({ queryKey: [...MATCH_KEY, 'iceBreakers', match_id] });
    },
  });
};

// === Phase 2.1: Feedback with Reasons ===
export const useSubmitMatchFeedbackWithReasons = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: SubmitMatchFeedbackWithReasonsData) =>
      matchService.submitMatchFeedbackWithReasons(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...MATCH_KEY, 'networkMatches'] });
      qc.invalidateQueries({ queryKey: [...MATCH_KEY, 'onboardingMatches'] });
      qc.invalidateQueries({ queryKey: [...MATCH_KEY, 'matchCounts'] });
    },
  });
};
